import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, X, Send, Download, Eye, MoveUp, MoveDown, Save, DollarSign, Ban } from "lucide-react";
import api, { FILES_BASE } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { toast } from "sonner";

const STATUS_STYLES = {
  Draft: "bg-slate-100 text-slate-600",
  Sent: "bg-blue-50 text-blue-700",
  Paid: "bg-emerald-50 text-emerald-700",
  Overdue: "bg-red-50 text-red-700",
  Cancelled: "bg-slate-100 text-slate-400 line-through",
};

const CURRENCIES = ["EUR", "USD", "GBP"];
const CURRENCY_SYMBOL = { EUR: "€", USD: "$", GBP: "£" };
const INPUT_CLASS = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none";
const INPUT_SM_CLASS = "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none";

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const fmt = (n, currency) => {
  const sym = CURRENCY_SYMBOL[currency] || "";
  return `${sym}${Number(n || 0).toFixed(2)}`;
};

const emptyLine = () => ({
  id: newId(),
  description: "",
  quantity: 1,
  unit_price: 0,
  tax_rate: 22,
});

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // { mode: 'create'|'edit', invoice }
  const [statusFilter, setStatusFilter] = useState("All");

  const load = async () => {
    try {
      const [invRes, clientRes] = await Promise.all([
        api.get("/invoices"),
        api.get("/clients"),
      ]);
      setInvoices(invRes.data);
      setClients(clientRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (statusFilter === "All") return invoices;
    return invoices.filter((i) => i.status === statusFilter);
  }, [invoices, statusFilter]);

  const totals = useMemo(() => {
    const paid = invoices.filter((i) => i.status === "Paid").reduce((s, i) => s + (i.amount_paid || i.total || 0), 0);
    const outstanding = invoices.filter((i) => ["Sent", "Overdue"].includes(i.status)).reduce((s, i) => s + (i.total || 0), 0);
    const drafts = invoices.filter((i) => i.status === "Draft").length;
    return { paid, outstanding, drafts };
  }, [invoices]);

  const openCreate = () => {
    if (clients.length === 0) {
      toast.error("Create a tenant first before making an invoice");
      return;
    }
    setEditor({
      mode: "create",
      invoice: {
        client_id: clients[0].id,
        issue_date: today(),
        due_date: addDays(14),
        currency: "EUR",
        line_items: [emptyLine()],
        notes: "",
        terms: "Payment due within 14 days.",
        bank_details: "",
      },
    });
  };

  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setEditor({ mode: "edit", invoice: { ...data, line_items: data.line_items.length ? data.line_items : [emptyLine()] } });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const send = async (id) => {
    if (!confirm("Send this invoice to the tenant? An email will be delivered.")) return;
    try {
      await api.post(`/invoices/${id}/send`);
      toast.success("Invoice sent");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markPaid = async (id) => {
    if (!confirm("Mark this invoice as fully paid?")) return;
    try {
      await api.patch(`/invoices/${id}`, { status: "Paid" });
      toast.success("Invoice marked as paid");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancel = async (id) => {
    if (!confirm("Cancel this invoice? This cannot be undone.")) return;
    try {
      await api.patch(`/invoices/${id}`, { status: "Cancelled" });
      toast.success("Invoice cancelled");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this invoice permanently?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Invoice deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadPdf = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}/pdf-url`);
      window.open(`${FILES_BASE}${data.url}`, "_blank", "noopener");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <p className="eyebrow mb-2">Billing</p>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="invoices-title">Invoices</h1>
          <p className="text-slate-500 text-sm mt-2">Create, send, and track invoices for your tenants.</p>
        </div>
        <button
          onClick={openCreate}
          className="bg-slate-900 text-white rounded-full px-5 py-2.5 font-medium hover:bg-slate-700 active:scale-95 inline-flex items-center gap-2"
          data-testid="new-invoice-button"
        >
          <Plus className="w-4 h-4" /> New invoice
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <SummaryCard label="Paid" value={fmt(totals.paid, "EUR")} testId="summary-paid" accent="emerald" />
        <SummaryCard label="Outstanding" value={fmt(totals.outstanding, "EUR")} testId="summary-outstanding" accent="blue" />
        <SummaryCard label="Drafts" value={totals.drafts} testId="summary-drafts" accent="slate" />
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-4 flex-wrap" data-testid="invoice-status-filters">
        {["All", "Draft", "Sent", "Paid", "Overdue", "Cancelled"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full ${statusFilter === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid={`filter-${s.toLowerCase()}`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="bento overflow-hidden" data-testid="invoices-list">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-6 py-4">Number</th>
              <th className="px-6 py-4">Tenant</th>
              <th className="px-6 py-4">Issued</th>
              <th className="px-6 py-4">Due</th>
              <th className="px-6 py-4 text-right">Total</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="7" className="px-6 py-16 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan="7" className="px-6 py-16 text-center text-slate-500">
                  No invoices yet. Click &quot;New invoice&quot; to create your first one.
                </td>
              </tr>
            )}
            {filtered.map((inv) => (
              <tr key={inv.id} className="border-t border-slate-100" data-testid={`invoice-row-${inv.id}`}>
                <td className="px-6 py-4 font-mono text-xs font-semibold text-slate-900">{inv.invoice_number}</td>
                <td className="px-6 py-4">
                  <p className="font-medium text-slate-900">{inv.client_name || "—"}</p>
                  <p className="text-xs text-slate-500">{inv.client_email}</p>
                </td>
                <td className="px-6 py-4 text-xs text-slate-500 font-mono">{inv.issue_date}</td>
                <td className="px-6 py-4 text-xs text-slate-500 font-mono">{inv.due_date}</td>
                <td className="px-6 py-4 text-right font-medium text-slate-900">{fmt(inv.total, inv.currency)}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[inv.status]}`}>{inv.status}</span>
                </td>
                <td className="px-6 py-4 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(inv.id)} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:bg-slate-100 px-2 py-1.5 rounded-lg" data-testid={`view-${inv.id}`}>
                    <Eye className="w-3.5 h-3.5" /> View
                  </button>
                  <button onClick={() => downloadPdf(inv.id)} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:bg-slate-100 px-2 py-1.5 rounded-lg ml-1" data-testid={`pdf-${inv.id}`}>
                    <Download className="w-3.5 h-3.5" /> PDF
                  </button>
                  {inv.status === "Draft" && (
                    <button onClick={() => send(inv.id)} className="inline-flex items-center gap-1 text-xs text-white bg-slate-900 hover:bg-slate-700 px-3 py-1.5 rounded-full ml-1" data-testid={`send-${inv.id}`}>
                      <Send className="w-3.5 h-3.5" /> Send
                    </button>
                  )}
                  {(inv.status === "Sent" || inv.status === "Overdue") && (
                    <button onClick={() => markPaid(inv.id)} className="inline-flex items-center gap-1 text-xs text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-full ml-1" data-testid={`mark-paid-${inv.id}`}>
                      <DollarSign className="w-3.5 h-3.5" /> Paid
                    </button>
                  )}
                  {["Sent", "Overdue"].includes(inv.status) && (
                    <button onClick={() => cancel(inv.id)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:bg-slate-100 px-2 py-1.5 rounded-lg ml-1" data-testid={`cancel-${inv.id}`}>
                      <Ban className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {(inv.status === "Draft" || inv.status === "Cancelled") && (
                    <button onClick={() => remove(inv.id)} className="inline-flex items-center gap-1 text-xs text-red-500 hover:bg-red-50 px-2 py-1.5 rounded-lg ml-1" data-testid={`delete-${inv.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editor && (
        <InvoiceEditor
          editor={editor}
          clients={clients}
          onClose={() => setEditor(null)}
          onSaved={async () => {
            setEditor(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent, testId }) {
  const accents = {
    emerald: "text-emerald-600",
    blue: "text-blue-600",
    slate: "text-slate-900",
  };
  return (
    <div className="bento p-6" data-testid={testId}>
      <p className="eyebrow mb-2">{label}</p>
      <p className={`text-3xl font-bold tracking-tight ${accents[accent] || ""}`}>{value}</p>
    </div>
  );
}

function InvoiceEditor({ editor, clients, onClose, onSaved }) {
  const [form, setForm] = useState(editor.invoice);
  const [saving, setSaving] = useState(false);
  const isEdit = editor.mode === "edit";
  const readOnly = isEdit && ["Paid", "Cancelled"].includes(form.status);

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    for (const li of form.line_items) {
      const net = Number(li.quantity || 0) * Number(li.unit_price || 0);
      const t = net * Number(li.tax_rate || 0) / 100;
      sub += net;
      tax += t;
    }
    return { sub: Number(sub.toFixed(2)), tax: Number(tax.toFixed(2)), total: Number((sub + tax).toFixed(2)) };
  }, [form.line_items]);

  const updateLine = (idx, patch) => {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((li, i) => (i === idx ? { ...li, ...patch } : li)),
    }));
  };
  const removeLine = (idx) => {
    setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));
  };
  const addLine = () => {
    setForm((f) => ({ ...f, line_items: [...f.line_items, emptyLine()] }));
  };
  const moveLine = (idx, delta) => {
    const t = idx + delta;
    if (t < 0 || t >= form.line_items.length) return;
    setForm((f) => {
      const arr = [...f.line_items];
      const [item] = arr.splice(idx, 1);
      arr.splice(t, 0, item);
      return { ...f, line_items: arr };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        client_id: form.client_id,
        issue_date: form.issue_date,
        due_date: form.due_date,
        currency: form.currency,
        notes: form.notes,
        terms: form.terms,
        bank_details: form.bank_details,
        line_items: form.line_items.map((li) => ({
          description: li.description,
          quantity: Number(li.quantity || 0),
          unit_price: Number(li.unit_price || 0),
          tax_rate: Number(li.tax_rate || 0),
        })),
      };
      if (payload.line_items.some((li) => !li.description.trim())) {
        toast.error("Every line item needs a description");
        setSaving(false);
        return;
      }
      if (isEdit) {
        const { client_id: _cid, ...editPayload } = payload;
        await api.patch(`/invoices/${form.id}`, editPayload);
      } else {
        await api.post("/invoices", payload);
      }
      toast.success(isEdit ? "Invoice updated" : "Invoice created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const currency = form.currency || "EUR";
  const symbol = CURRENCY_SYMBOL[currency] || "";

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-3xl p-8 max-w-3xl w-full shadow-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="invoice-editor">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="eyebrow mb-1">
              {isEdit ? `Invoice ${form.invoice_number}` : "Draft"} · <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_STYLES[form.status || "Draft"]}`}>{form.status || "Draft"}</span>
            </p>
            <h2 className="text-2xl font-bold tracking-tight">{isEdit ? "Invoice" : "New invoice"}</h2>
            {readOnly && (
              <p className="text-xs text-amber-600 mt-1">Read-only — {form.status} invoices cannot be edited.</p>
            )}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full" data-testid="close-editor"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Tenant</label>
            <select
              disabled={isEdit}
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className={INPUT_CLASS}
              data-testid="invoice-client"
            >
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name} · {c.email}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Currency</label>
            <select
              disabled={readOnly}
              value={currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              className={INPUT_CLASS}
              data-testid="invoice-currency"
            >
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Issue date</label>
            <input
              type="date"
              disabled={readOnly}
              value={form.issue_date}
              onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
              className={INPUT_CLASS}
              data-testid="invoice-issue-date"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Due date</label>
            <input
              type="date"
              disabled={readOnly}
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              className={INPUT_CLASS}
              data-testid="invoice-due-date"
            />
          </div>
        </div>

        <label className="text-sm font-medium text-slate-700 mb-1.5 block">Line items</label>
        <div className="border border-slate-200 rounded-2xl overflow-hidden mb-4">
          <div className="grid grid-cols-[1fr_70px_100px_70px_100px_auto] gap-2 px-3 py-2 bg-slate-50 text-xs uppercase tracking-widest text-slate-500 font-medium">
            <div>Description</div>
            <div className="text-right">Qty</div>
            <div className="text-right">Unit</div>
            <div className="text-right">Tax %</div>
            <div className="text-right">Line total</div>
            <div />
          </div>
          {form.line_items.map((li, idx) => {
            const lineNet = Number(li.quantity || 0) * Number(li.unit_price || 0);
            const lineTotal = lineNet * (1 + Number(li.tax_rate || 0) / 100);
            return (
              <div key={li.id} className="grid grid-cols-[1fr_70px_100px_70px_100px_auto] gap-2 px-3 py-2 border-t border-slate-100 items-center" data-testid={`invoice-line-${idx}`}>
                <input
                  disabled={readOnly}
                  value={li.description}
                  onChange={(e) => updateLine(idx, { description: e.target.value })}
                  placeholder="e.g. Brand identity design"
                  className={INPUT_SM_CLASS}
                  data-testid={`line-description-${idx}`}
                />
                <input
                  disabled={readOnly}
                  type="number"
                  step="any"
                  min="0"
                  value={li.quantity}
                  onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                  className={`${INPUT_SM_CLASS} text-right`}
                  data-testid={`line-quantity-${idx}`}
                />
                <input
                  disabled={readOnly}
                  type="number"
                  step="any"
                  min="0"
                  value={li.unit_price}
                  onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                  className={`${INPUT_SM_CLASS} text-right`}
                  data-testid={`line-price-${idx}`}
                />
                <input
                  disabled={readOnly}
                  type="number"
                  step="any"
                  min="0"
                  value={li.tax_rate}
                  onChange={(e) => updateLine(idx, { tax_rate: e.target.value })}
                  className={`${INPUT_SM_CLASS} text-right`}
                  data-testid={`line-tax-${idx}`}
                />
                <div className="text-right text-sm font-medium text-slate-900 pr-2">
                  {symbol}{lineTotal.toFixed(2)}
                </div>
                <div className="flex gap-0.5">
                  <button type="button" disabled={readOnly || idx === 0} onClick={() => moveLine(idx, -1)} className="p-1 text-slate-400 hover:bg-slate-100 rounded disabled:opacity-30">
                    <MoveUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" disabled={readOnly || idx === form.line_items.length - 1} onClick={() => moveLine(idx, 1)} className="p-1 text-slate-400 hover:bg-slate-100 rounded disabled:opacity-30">
                    <MoveDown className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" disabled={readOnly} onClick={() => removeLine(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded disabled:opacity-30">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
          {!readOnly && (
            <button
              type="button"
              onClick={addLine}
              className="w-full py-3 text-sm font-medium text-slate-600 hover:bg-slate-50 border-t border-slate-100 inline-flex items-center justify-center gap-1"
              data-testid="add-line-item"
            >
              <Plus className="w-3.5 h-3.5" /> Add line item
            </button>
          )}
        </div>

        {/* Totals */}
        <div className="flex justify-end mb-6">
          <div className="w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{symbol}{totals.sub.toFixed(2)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Tax</span><span>{symbol}{totals.tax.toFixed(2)}</span></div>
            <div className="flex justify-between text-slate-900 font-bold text-lg pt-1 border-t border-slate-200"><span>Total</span><span>{symbol}{totals.total.toFixed(2)}</span></div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Bank / payment details (on PDF)</label>
            <textarea
              disabled={readOnly}
              rows={3}
              value={form.bank_details}
              onChange={(e) => setForm({ ...form, bank_details: e.target.value })}
              placeholder="IBAN IT60X0542811101000000123456&#10;SWIFT/BIC BLOPIT22"
              className={`${INPUT_CLASS} resize-none font-mono text-xs`}
              data-testid="invoice-bank-details"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Notes (visible to tenant)</label>
            <textarea
              disabled={readOnly}
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Thank you for your business!"
              className={`${INPUT_CLASS} resize-none`}
              data-testid="invoice-notes"
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Terms (footer of PDF)</label>
          <input
            disabled={readOnly}
            value={form.terms}
            onChange={(e) => setForm({ ...form, terms: e.target.value })}
            className={INPUT_CLASS}
            data-testid="invoice-terms"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full font-medium hover:bg-slate-100 text-sm" data-testid="cancel-editor">
            Close
          </button>
          {!readOnly && (
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-700 rounded-full px-5 py-2.5 active:scale-95 disabled:opacity-50"
              data-testid="save-invoice"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : isEdit ? "Save changes" : "Create draft"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
